import { type KeyboardEvent, type ReactElement, useEffect, useRef } from "react"
import styled from "styled-components"
import { CheckIcon } from "~/shared/ui/icons"
import { PLAYBACK_RATES, type PlaybackRate } from "../types"

const Panel = styled.div`
  position: absolute;
  right: 0;
  bottom: calc(100% + ${({ theme }) => theme.spacing.md});
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
  width: 20rem;
  max-width: calc(100vw - ${({ theme }) => theme.spacing.xl});
  padding: ${({ theme }) => theme.spacing.md};
  border: 2px solid ${({ theme }) => theme.player.panelBorder};
  border-radius: 1rem;
  background-color: ${({ theme }) => theme.player.panel};
  box-shadow: 0 0 20px 0 rgb(0 0 0 / 0.35);
  z-index: ${({ theme }) => theme.zIndex.overlay};
`

const Title = styled.h2`
  font-size: ${({ theme }) => theme.fontSize.xl};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  line-height: ${({ theme }) => theme.lineHeight.tight};
  text-align: center;
  color: ${({ theme }) => theme.player.textPrimary};
`

const Options = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
`

/**
 * `aria-checked` rather than a `:checked` selector: this is a radio group of
 * buttons, and the selected row is the one drawn with the brand border and the
 * check glyph.
 */
const Option = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.xs};
  width: 100%;
  padding: ${({ theme }) => `${theme.spacing.xs} ${theme.spacing.sm}`};
  border: 1px solid ${({ theme }) => theme.player.panelBorder};
  border-radius: ${({ theme }) => theme.radius.sm};
  background-color: ${({ theme }) => theme.player.tile};
  color: ${({ theme }) => theme.player.textPrimary};
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  line-height: ${({ theme }) => theme.lineHeight.normal};
  text-align: left;
  cursor: pointer;
  transition: border-color 120ms ease-in-out, background-color 120ms ease-in-out;

  &[aria-checked="true"] {
    border-width: 2px;
    border-color: ${({ theme }) => theme.player.brand};
  }

  &:hover {
    background-color: ${({ theme }) => theme.player.panelBorder};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.player.focusRing};
    outline-offset: 2px;
  }
`

interface PlaybackSpeedPanelProps {
  readonly value: PlaybackRate
  readonly onChange: (rate: PlaybackRate) => void
  readonly onClose: () => void
  readonly qaHook: string
}

/**
 * The Playback Speed panel from node 400-3126.
 *
 * Focus moves to the selected rate on open and returns to the trigger on close,
 * which the trigger owns — a viewer who opened this from the keyboard must not be
 * dropped back at the top of the page when it closes.
 */
export const PlaybackSpeedPanel = ({ value, onChange, onClose, qaHook }: PlaybackSpeedPanelProps): ReactElement => {
  const selectedRef = useRef<HTMLButtonElement | null>(null)

  useEffect(() => {
    selectedRef.current?.focus()
  }, [])

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      event.stopPropagation()
      onClose()
    }
  }

  return (
    <Panel role="dialog" aria-label="Playback speed" data-qa-hook={qaHook} onKeyDown={handleKeyDown}>
      <Title>Playback Speed</Title>

      <Options role="radiogroup" aria-label="Playback speed">
        {PLAYBACK_RATES.map(rate => (
          <Option
            key={rate}
            ref={rate === value ? selectedRef : undefined}
            type="button"
            role="radio"
            aria-checked={rate === value}
            data-qa-hook={`${qaHook}-option-${rate}`}
            onClick={() => {
              onChange(rate)
              onClose()
            }}
          >
            <span>{`${rate}x`}</span>
            {rate === value ? <CheckIcon size={20} /> : null}
          </Option>
        ))}
      </Options>
    </Panel>
  )
}
